// ===========================================
// Family credit API (#642)
// ===========================================
// A family's account credit: overpayments held, applied to invoices, flagged for refund and
// refunded. Every write returns the refreshed panel. Money arrives as 2-dp strings; dates as
// YYYY-MM-DD school days (`occurredOn`, a @db.Date: render with UTC getters).

import { apiClient } from '../services/api';

interface ApiResponse<T> {
  status: string;
  data: T;
}

export type FamilyCreditKind = 'ADDED' | 'APPLIED' | 'REFUND_DUE' | 'REFUNDED';

export interface FamilyCreditEntry {
  id: string;
  kind: FamilyCreditKind;
  amount: string;
  occurredOn: string;
  note: string | null;
  refundReference: string | null;
  refundDueEntryId: string | null;
  /** REFUND_DUE: still waiting to be paid back. */
  open: boolean;
  invoice: { id: string; invoiceNumber: string } | null;
  paymentId: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  createdAt: string;
  /** The owner's view only; null for everyone else (and for a movement the system made). */
  createdByName: string | null;
  voidedByName: string | null;
}

export interface FamilyCredit {
  familyId: string;
  balance: string;
  refundDue: string;
  available: string;
  entries: FamilyCreditEntry[];
}

export interface RefundDue {
  id: string;
  familyId: string;
  familyName: string;
  /** For the family page's `?parent=` deep link. */
  parentId: string | null;
  amount: string;
  occurredOn: string;
  note: string | null;
  createdByName: string | null;
}

export const familyCreditApi = {
  get: async (familyId: string): Promise<FamilyCredit> =>
    (await apiClient.get<ApiResponse<FamilyCredit>>(`/invoices/admin/families/${familyId}/credit`)).data,

  /** Take account credit off the invoice it was applied to (operations_invoices.delete). */
  takeOff: async (entryId: string, reason: string): Promise<FamilyCredit> =>
    (await apiClient.post<ApiResponse<FamilyCredit>>(`/invoices/admin/credit-entries/${entryId}/take-off`, { reason }))
      .data,

  /** Held credit → Refund due (operations_refunds.write). */
  flagRefundDue: async (familyId: string, amount: number, note: string): Promise<FamilyCredit> =>
    (
      await apiClient.post<ApiResponse<FamilyCredit>>(`/invoices/admin/families/${familyId}/credit/refund-due`, {
        amount,
        note,
      })
    ).data,

  /** Mark a Refund due as paid back (operations_refunds.write). */
  markRefunded: async (
    entryId: string,
    body: { refundedOn: string; reference: string; note?: string }
  ): Promise<FamilyCredit> =>
    (await apiClient.post<ApiResponse<FamilyCredit>>(`/invoices/admin/credit-entries/${entryId}/refunded`, body)).data,

  /** A Refund due back to credit, or void a Refunded record (operations_refunds.write). */
  voidRefundEntry: async (entryId: string, reason: string): Promise<FamilyCredit> =>
    (await apiClient.post<ApiResponse<FamilyCredit>>(`/invoices/admin/credit-entries/${entryId}/void`, { reason }))
      .data,

  /** Every open Refund due of the school (the dashboard card). */
  refundsDue: async (): Promise<RefundDue[]> =>
    (await apiClient.get<ApiResponse<RefundDue[]>>('/invoices/admin/credit/refunds-due')).data,

  // ---- #642 Phase 2 ----

  /** The family credit settings (operations_invoices.read; the owner changes them). */
  getSettings: async (): Promise<FamilyCreditSettings> =>
    (await apiClient.get<ApiResponse<FamilyCreditSettings>>('/admin/settings/family-credit')).data,
  updateSettings: async (patch: Partial<FamilyCreditSettings>): Promise<FamilyCreditSettings> =>
    (await apiClient.put<ApiResponse<FamilyCreditSettings>>('/admin/settings/family-credit', patch)).data,
  getFinancialYear: async (): Promise<FinancialYearSettings> =>
    (await apiClient.get<ApiResponse<FinancialYearSettings>>('/admin/settings/financial-year')).data,
  updateFinancialYear: async (patch: FinancialYearSettings): Promise<FinancialYearSettings> =>
    (await apiClient.put<ApiResponse<FinancialYearSettings>>('/admin/settings/financial-year', patch)).data,

  /** Request 8: the family credit report (owner only). */
  report: async (filters: FamilyCreditReportFilters): Promise<FamilyCreditReport> =>
    (await apiClient.get<ApiResponse<FamilyCreditReport>>('/reports/family-credit', { params: filters })).data,
  exportReport: async (filters: FamilyCreditReportFilters): Promise<Blob> =>
    (await apiClient.getRaw<Blob>('/reports/family-credit/export', { params: filters, responseType: 'blob' })).data,
};

/** #642 Phase 2 (GET/PUT /admin/settings/family-credit). */
export interface FamilyCreditSettings {
  reviewAfterMonths: number;
  termsLine: string;
}

/** Shared with #374 (GET/PUT /admin/settings/financial-year): the year starts on the 1st of this month. */
export interface FinancialYearSettings {
  startMonth: number;
}

/** A financial year (named by the year it starts in) OR an inclusive day range (YYYY-MM-DD). */
export interface FamilyCreditReportFilters {
  fy?: number;
  from?: string;
  to?: string;
}

export interface FamilyCreditReportRow {
  familyId: string;
  familyName: string;
  parentId: string | null;
  balance: string;
  refundDue: string;
  available: string;
  firstAddedOn: string | null;
  lastMovementOn: string | null;
  addedInPeriod: string;
  appliedInPeriod: string;
  refundedInPeriod: string;
  needsReview: boolean;
}

/** Mirrors services/reports/familyCredit.report.ts (asserted, never validated: keep in step). */
export interface FamilyCreditReport {
  period: { from: string | null; to: string | null; label: string };
  financialYear: { current: number; startMonth: number; label: string };
  reviewAfterMonths: number;
  families: FamilyCreditReportRow[];
  totals: {
    balance: string;
    refundDue: string;
    available: string;
    addedInPeriod: string;
    appliedInPeriod: string;
    refundedInPeriod: string;
    needsReview: number;
  };
}
